/*
 * INDEX.JSX - AddProject Component for Project and Tender Creation
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.convertToProject] - If true, disables some fields and enables convert-to-project flow.
 * @param {boolean} [props.convertTender] - If true, enables convert tender flow and disables some validations.
 * @param {string} [props.projectId] - Project ID for edit mode (optional).
 * @param {Object} [props.permissions] - User permissions for field access.
 * @param {Function} [props.handleClose] - Handler to close the modal/dialog (used in convert flow).
 * @param {string} [props.projectType] - Project type for navigation fallback.
 *
 * SUMMARY:
 * AddProject component that handles form state, validation, and submission for project
 * details including manager, customer, address, and attachments. Supports both add and
 * edit flows with dynamic form fields and integration with related entities.
 *
 */

import idx from "idx";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "../scss/project-management.scss";
import _ from "lodash";
import { getProjectData } from "../utils/get-project";
import { updateProjectSummaryData } from "../utils/get-project-summary";
import { resolveProjectType } from "../utils/resolve-project-type";
import { errorSnackBar } from "../../../actions/common.action";
import "../scss/task.scss";
import {
  PROJECT_MANAGEMENT,
  CUSTOMER_MANAGEMENT,
  ADD_CUSTOMER,
} from "../../../constants/route-paths";
import { getUserList } from "../../../actions/user-management.action";
import {
  addProject,
  editProject,
  addStage,
} from "../../../actions/project-management.action";
import DeleteIcon from "@mui/icons-material/Delete";
import { getGlobalSetting } from "../../../actions/global-setting.action";
import { getLevels } from "../../../actions/inventory.action";
import Cookies from "js-cookie";
import FileUploader from "../../../components/file-uploader";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import AddIcon from "@mui/icons-material/Add";
import DatePickerInput from "../../../components/inputs/date-picker";
import Button from "react-bootstrap/Button";
import CustomInput from "../../../components/inputs/new-input";
import SelectInput from "../../../components/inputs/select";
import TextAreaInput from "../../../components/inputs/textarea-input";
import { cleaner } from "../../../util/validators/cleaners";
import { AddProjectFields } from "./data/fields";
import { getCustomerList } from "../../../actions/customer-management.action";
import { clearProjectAction } from "../../../slice/project-summary";
import { fileValidator } from "../../../util/validators/file";
import { setCookie } from "../../../util/auth";
import { ValidateField, ValidatorTypes } from "../../../util/validators";
import { Checkbox } from "@mui/material";
import { PROJECT_STATUS, PROJECT_TYPE, QUOTE_TYPE } from "../../../util/enums";

export default function AddProject(props) {
  // --- State and Redux hooks ---
  const [addProjectState, setAddProjectState] = useState({
    convertTender: false,
    edit: false,
    projectNextStep: false,
    showTemplateModal: false,
    showConfirmConvertModal: false,
  });
  const [formField, setFormField] = useState(_.cloneDeep(AddProjectFields));
  const permissions = props.permissions || {};
  let { projectId /*step*/ } = useParams();
  if (!projectId) projectId = props.projectId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const entitlements = useSelector((state) => state.user.entitlements);
  const projectSummary = useSelector((state) => state.projectSummary);
  const project = useSelector((state) => state.project);
  const customerData = useSelector((state) => state.customerData);

  // Check if any section has attribution enabled (passed from ProjectMenu)
  const hasAttributionEnabled = props.hasAttributionEnabled || false;

  // --- Initial load: fetch project, customer, and entitlement data ---
  useEffect(() => {
    initLoad();
  }, [projectId]);

  // --- Disable budget field if converting to project ---
  useEffect(() => {
    if (props.convertToProject && !formField.budget.disable) {
      setFormField((prev) => ({
        ...prev,
        budget: { ...prev.budget, disable: props.convertToProject || false },
      }));
    }
  }, [props.convertToProject]);

  // --- Populate project categories from project data ---
  useEffect(() => {
    if (
      (idx(project, (_) => _.projectCategories) || []).length &&
      !formField.category.items.length
    ) {
      const projectcategories = [];
      (idx(project, (_) => _.projectCategories) || []).map((category) =>
        projectcategories.push({ key: category.name, value: category.type }),
      );
      let project_type = searchParams.get("project-type");
      let _category = projectcategories.find(
        (item) => item.value == project_type,
      );
      setFormField((prev) => ({
        ...prev,
        category: {
          ...prev.category,
          items: projectcategories,
          value: _category?.value || project_type,
        },
      }));
    }
  }, [project, formField.category.items]);

  // --- If editing, load project summary and prefill form fields ---
  useEffect(() => {
    if (
      projectId &&
      idx(projectSummary, (_) => _.project.res.id) &&
      formField.projectName.value !=
      idx(projectSummary, (_) => _.project.res.name)
    ) {
      _editProject();
    }
  }, [projectSummary]);

  /**
   * Initialize form: fetch customers, allowed project types, levels, managers, and prefill if converting tender.
   */
  const initLoad = async () => {
    setCookie({ name: "projectId", value: projectId });
    if (!customerData.dropdown.length)
      dispatch(getCustomerList("business_name,first_name,name")).catch((e) =>
        console.error(e),
      );
    let allowedProjectTypes = [];
    if (entitlements.currentProject)
      allowedProjectTypes.push({ value: "noquote", key: "Cost Plus" });
    if (entitlements.quotesProject)
      allowedProjectTypes.push({ value: "quote", key: "Fixed Price Quote" });
    setFormField((prev) => ({
      ...prev,
      projectQuoteType: {
        ...prev.projectQuoteType,
        items: allowedProjectTypes,
      },
    }));
    dispatch(getLevels()).then((res) => {
      const levels = [];
      (res || []).map((level) =>
        levels.push({ key: level.name, value: level.id }),
      );
      setFormField((prev) => ({
        ...prev,
        level: { ...prev.level, items: levels },
      }));
    });

    dispatch(getUserList()).then((users) => {
      const data = users.map((user) => {
        return {
          value: user.id,
          key:
            (idx(user, (_) => _.firstName) || "") +
            " " +
            (idx(user, (_) => _.lastName) || ""),
        };
      });
      setFormField((prev) => ({
        ...prev,
        manager: { ...prev.manager, items: data },
      }));
    });
    const convertTender = props.convertTender || false;
    if (convertTender) {
      setFormField((prev) => ({
        ...prev,
        manager: {
          ...prev.manager,
          validation: prev.manager.validation.splice(
            formField.projectQuoteType.value == "quote" ? 0 : 1,
            1,
          ),
        },
        startDate: {
          ...prev.startDate,
          validation: prev.startDate.validation.splice(1, 1),
        },
        budget: {
          ...prev.budget,
          validation: prev.budget.validation.splice(1, 1),
        },
      }));
    }
    setAddProjectState((prev) => ({
      ...prev,
      convertTender,
    }));
    if (projectId) {
      await updateProjectSummaryData({ projectId, dispatch });
    } else {
      dispatch(clearProjectAction());
      dispatch(getGlobalSetting("FINANCE_TAX")).then((res) => {
        if (res && res?.data) {
          const show_retentions = res?.data?.finance_show_retentions;
          const show_variations = res?.data?.finance_show_variations;
          setFormField((prev) => ({
            ...prev,
            showRetentions: {
              ...prev.showRetentions,
              value: prev.showRetentions.touched
                ? prev.showRetentions.value
                : show_retentions,
            },
            showVariations: {
              ...prev.showVariations,
              value: prev.showVariations.touched
                ? prev.showVariations.value
                : show_variations,
            },
          }));
        }
      });
    }

    // let fields = _.cloneDeep(formField);
    const redirect = searchParams.get("redirect");
    // const projectType = searchParams.get("project-type");

    if (redirect && sessionStorage.getItem("fields")) {
      let fields = JSON.parse(sessionStorage.getItem("fields"));
      Cookies.remove("url");
      Cookies.remove("fields");
      setFormField((prev) => ({
        ...prev,
        profitMarginValue: {
          ...prev.profitMarginValue,
          value: fields.profitMarginValue.value || prev.profitMarginValue.value,
        },
        prefix: {
          ...prev.prefix,
          value: fields.prefix.value || prev.prefix.value,
        },
        suffix: {
          ...prev.suffix,
          value: fields.suffix.value || prev.suffix.value,
        },
        projectName: {
          ...prev.projectName,
          value: fields.projectName.value || prev.projectName.value,
        },
        address: {
          ...prev.address,
          value: fields.address.value || prev.address.value,
        },
        manager: {
          ...prev.manager,
          value: fields.manager.value || prev.manager.value,
        },
        customer: {
          ...prev.customer,
          value: fields.customer.value || prev.customer.value,
        },
        ...(fields.contact.value
          ? {
            contact: {
              ...prev.contact,
              value: fields.contact.value || prev.contact.value,
            },
          }
          : {}),
        date: { ...prev.date, value: fields.date.value || prev.date.value },
        startDate: {
          ...prev.startDate,
          value: fields.startDate.value || prev.startDate.value,
        },
        budget: {
          ...prev.budget,
          value: fields.budget.value || prev.budget.value,
        },
        level: { ...prev.level, value: fields.level.value || prev.level.value },
        category: {
          ...prev.category,
          value: fields.category.value || prev.category.value,
        },
        projectQuoteType: {
          ...prev.projectQuoteType,
          value: fields.projectQuoteType.value || prev.projectQuoteType.value,
        },
        projectType: {
          ...prev.projectType,
          value: fields.projectType.value || prev.projectType.value,
        },
        description: {
          ...prev.description,
          value: fields.description.value || prev.description.value,
        },
        exclusions: {
          ...prev.exclusions,
          value: fields.exclusions.value || prev.exclusions.value,
        },
        showRetentions: {
          ...prev.showRetentions,
          value: fields.showRetentions?.value ?? prev.showRetentions.value,
          touched: fields.showRetentions?.touched ?? prev.showRetentions.touched,
        },
        showVariations: {
          ...prev.showVariations,
          value: fields.showVariations?.value ?? prev.showVariations.value,
          touched: fields.showVariations?.touched ?? prev.showVariations.touched,
        }
      }));
    }
  };

  /**
   * Load project data for editing, prefill form fields from project summary.
   */
  const _editProject = async () => {
    const project = idx(projectSummary, (_) => _.project.res);
    const status = project.type || "TENDER";
    const calculations = idx(projectSummary, (_) => _.projectTotal) || {};

    let attachments = [];
    project.attachment &&
      project.attachment.forEach((attach) => {
        if (_.isString(attach)) {
          attachments.push(JSON.parse(attach));
        } else {
          attachments.push(attach);
        }
      });

    let siteAddress = [];
    if (idx(project, (_) => _.customer.id)) {
      siteAddress = getAddressDropdown(project.customer.id);
    }
    let _projectAddress = cleaner(project.address).toLowerCase();
    const selectedAddress = siteAddress.find(
      (item) =>
        item.key.toLowerCase() == _projectAddress ||
        item.value.toLowerCase() == _projectAddress,
    );

    const projectShowRetentions =
      typeof project.show_retentions === "boolean"
        ? project.show_retentions
        : typeof project.showRetentions === "boolean"
          ? project.showRetentions
          : false;
    const projectShowVariations =
      typeof project.show_variations === "boolean"
        ? project.show_variations
        : typeof project.showVariations === "boolean"
          ? project.showVariations
          : false;

    setFormField((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        value: selectedAddress?.value,
      },
      contact: {
        ...prev.contact,
        value: idx(project, (_) => _.customerContact.id) || "",
      },
      manager: {
        ...prev.manager,
        value: project.managerId || "",
        validation:
          status === "PROJECT"
            ? prev.projectQuoteType.value == "quote"
              ? prev.manager.validation.splice(0, 1)
              : prev.manager.validation.splice(1, 1)
            : prev.manager.validation,
      },
      startDate: {
        ...prev.startDate,
        value: project.startDate ? new Date(project.startDate) : "",
        validation:
          status === "PROJECT"
            ? prev.startDate.validation.splice(1, 1)
            : prev.startDate.validation,
      },
      budget: {
        ...prev.budget,
        value: props.convertTender
          ? (calculations.totalSaleAmount || 0).toFixed(2)
          : Number(project.budget || 0) || "",
        validation:
          status === "PROJECT"
            ? prev.budget.validation.splice(1, 1)
            : prev.budget.validation,
      },
      exclusions: { ...prev.exclusions, value: project.exclusions },
      description: { ...prev.description, value: project.description },
      projectQuoteType: {
        ...prev.projectQuoteType,
        value: project.quoteType || "",
      },
      projectName: { ...prev.projectName, value: project.name },
      level: { ...prev.level, value: project.level },
      category: { ...prev.category, value: project.projectType },
      customer: {
        ...prev.customer,
        value: idx(project, (_) => _.customer.id) || "",
      },
      date: { ...prev.date, value: new Date(project.dateOfCreation) },
      projectType: { ...prev.projectType, value: project.type },
      profitMarginValue: {
        ...prev.profitMarginValue,
        value: project.profitMarginValue || "",
      },
      prefix: { ...prev.prefix, value: project.prefix || "" },
      suffix: { ...prev.suffix, value: project.suffix || "" },
      autoCalculateRetention: {
        ...prev.autoCalculateRetention,
        value: project.autoCalculateRetention || false,
      },
      showRetentions: { ...prev.showRetentions, value: projectShowRetentions },
      showVariations: { ...prev.showVariations, value: projectShowVariations },
      useRevisedProjectTotal: {
        ...prev.useRevisedProjectTotal,
        value: project.useRevisedProjectTotal || false,
      },
      maxRetentionPercentage: {
        ...prev.maxRetentionPercentage,
        value: project.maxRetentionPercentage || 5,
      },
      defaultRetentionPercentage: {
        ...prev.defaultRetentionPercentage,
        value: project.defaultRetentionPercentage || 10,
      },
      attachments: { ...prev.attachments, attachmentArr: attachments },
      address_breakdown: {
        ...prev.address_breakdown,
        value: project.address_breakdown || null,
      },
    }));

    setAddProjectState((prev) => ({
      ...prev,
      edit: true,
    }));
  };

  /**
   * Cancel handler: navigates back to project list with correct type/category.
   */
  const cancelHandler = () => {
    let type = resolveProjectType(
      idx(projectSummary, (_) => _.project.projectDataType) ||
      searchParams.get("project-type") ||
      props.projectType,
      project
    );
    const category =
      searchParams.get("project-category") ||
      idx(projectSummary, (_) => _.project.projectType);

    let url = `/${PROJECT_MANAGEMENT}?project-type=${type}`;
    if (category) {
      url += `&project-category=${category}`;
    }
    navigate(url);
  };

  /**
   * Returns true if convertToProject prop is set.
   */
  const isConvertToProject = () => {
    return props.convertToProject ? true : false;
  };

  /**
   * Handles address selection and populates address_breakdown
   * @param {Event} e
   */
  const inputChangeAddress = (e) => {
    const { error, valid } = ValidateField({
      value: e.target.value,
      validation: formField[e.target.name].validation,
    });

    // Get the full customer site data for address_breakdown
    const customer =
      (idx(customerData, (_) => _.customers) || []).find(
        (customer) => customer.id === formField.customer.value,
      ) || {};
    const selectedSite =
      (customer.siteAddress || []).find((site) => site.id === e.target.value) ||
      {};

    // Build address_breakdown object
    const addressBreakdown = {
      address_line_1: selectedSite.address_line_1 || "",
      address_line_2: selectedSite.address_line_2 || "",
      suburb: selectedSite.suburb || "",
      state: selectedSite.state || "",
      postcode: selectedSite.postcode || "",
      country: selectedSite.country || "",
      site_name: selectedSite.name || "",
      formatted_address: [
        selectedSite.address_line_1,
        selectedSite.address_line_2,
        selectedSite.suburb,
        selectedSite.state,
        selectedSite.country,
        selectedSite.postcode,
      ]
        .filter((item) => item)
        .join(" ")
        .trim(),
    };

    setFormField((prev) => ({
      ...prev,
      [e.target.name]: {
        ...prev[e.target.name],
        value: e.target.value,
        touched: true,
        error: error,
        valid: valid,
      },
      address_breakdown: {
        ...prev.address_breakdown,
        value: addressBreakdown,
      },
    }));
  };

  /**
   * Handles input changes for all fields, including file uploads and validation.
   * @param {Event} e
   */
  const inputChangeHandler = (e) => {
    if (e.target.type === "file") {
      const fileToUpload = e.target.files;
      Array.isArray(fileToUpload) &&
        fileToUpload.forEach((file) => {
          const fileValidation = fileValidator({ file, acceptedFileSize: 10 });
          const { validFileSize, validFileType, error } = fileValidation;
          if (!validFileSize.valid && !error)
            dispatch(errorSnackBar(validFileSize.error));
          else if (!validFileType.valid && !error)
            dispatch(errorSnackBar(validFileType.error));
          else if (error) dispatch(errorSnackBar(error.error));
          else formField[e.target.name].value.push(file);
        });
    } else if (e.target.type === "checkbox") {
      formField[e.target.name].value = e.target.checked;
    } else {
      formField[e.target.name].value = e.target.value;
    }
    formField[e.target.name].touched = true;
    let { error, valid } = ValidateField({
      value: formField[e.target.name].value,
      validation: formField[e.target.name].validation,
    });
    // Additional field-specific validation for retention minimums
    if (e.target.name === "maxRetentionPercentage") {
      const num = Number(formField[e.target.name].value);
      if (formField[e.target.name].value !== "" && !isNaN(num) && num < 5) {
        error = "Value must be at least 5";
        valid = false;
      }
    } else if (e.target.name === "defaultRetentionPercentage") {
      const num = Number(formField[e.target.name].value);
      if (formField[e.target.name].value !== "" && !isNaN(num) && num < 10) {
        error = "Value must be at least 10";
        valid = false;
      }
    }
    formField[e.target.name].error = error;
    formField[e.target.name].valid = valid;
    if (formField.projectQuoteType.value == "quote") {
      formField.manager.validation.splice(0, 1);
    } else {
      formField.manager.validation.splice(1, 1);
    }
    setFormField((prev) => ({
      ...prev,
      ...formField,
    }));
  };

  /**
   * Handles customer selection, updates dependent fields (level, address, contact).
   * @param {Event} e
   */
  const inputChangeCustomer = async (e) => {
    const Customer =
      (idx(customerData, (_) => _.customers) || []).find(
        (customer) => customer.id === e.target.value,
      ) || {};
    const { error, valid } = ValidateField({
      value: e.target.value,
      validation: formField[e.target.name].validation,
    });
    setFormField((prev) => ({
      ...prev,
      ...formField,
      level: { ...prev.level, value: Customer?.level_id || "" },
      address: {
        ...prev.address,
        value:
          (Customer.siteAddress || []).length == 1
            ? idx(Customer, (_) => _.siteAddress[0].id)
            : "",
      },
      address_breakdown: {
        ...prev.address_breakdown,
        value:
          (Customer.siteAddress || []).length == 1
            ? {
              address_line_1: Customer.siteAddress[0].address_line_1 || "",
              address_line_2: Customer.siteAddress[0].address_line_2 || "",
              suburb: Customer.siteAddress[0].suburb || "",
              state: Customer.siteAddress[0].state || "",
              postcode: Customer.siteAddress[0].postcode || "",
              country: Customer.siteAddress[0].country || "",
              site_name: Customer.siteAddress[0].name || "",
              formatted_address: [
                Customer.siteAddress[0].address_line_1,
                Customer.siteAddress[0].address_line_2,
                Customer.siteAddress[0].suburb,
                Customer.siteAddress[0].state,
                Customer.siteAddress[0].country,
                Customer.siteAddress[0].postcode,
              ]
                .filter((item) => item)
                .join(" ")
                .trim(),
            }
            : null,
      },
      contact: {
        ...prev.contact,
        value:
          (Customer.contacts || []).length == 1
            ? idx(Customer, (_) => _.contacts[0].id)
            : "",
      },
      [e.target.name]: {
        ...prev[e.target.name],
        value: e.target.value,
        touched: true,
        error: error,
        valid: valid,
      },
    }));
  };

  /**
   * Returns dropdown options for customer contacts.
   * @param {string} [value] - Customer ID
   * @returns {Array<{value: string, key: string}>}
   */
  const getContactDropdown = (value) => {
    const Customer =
      (idx(customerData, (_) => _.customers) || []).find(
        (customer) => customer.id === (value || formField.customer.value),
      ) || {};
    const data = (Customer.contacts || []).map((contact) => {
      return {
        value: contact.id,
        key: contact.firstName + " " + contact.lastName,
      };
    });
    return data;
  };

  /**
   * Returns dropdown options for customer site addresses.
   * @param {string} [value] - Customer ID
   * @returns {Array<{value: string, key: string}>}
   */
  const getAddressDropdown = (value) => {
    const Customer =
      (idx(customerData, (_) => _.customers) || []).find(
        (customer) => customer.id === (value || formField.customer.value),
      ) || {};
    const data = (Customer.siteAddress || []).map((site) => {
      return {
        value: site.id,
        key: site.name || site.f_address,
      };
    });
    return data;
  };

  /**
   * Navigates to add or edit customer, saving current form state for redirect.
   * @param {string} [id] - Customer ID for edit
   */
  const addNewCustomer = (id) => {
    let { pathname, search } = window.location;
    sessionStorage.setItem("url", `${pathname}${search}`);
    sessionStorage.setItem("fields", JSON.stringify(formField));
    navigate(`/${CUSTOMER_MANAGEMENT}/${id ? "edit" : ADD_CUSTOMER}${id ? `/${id}` : ""}?redirect=true`,);
  };

  /**
   * Handles date input changes for date/startDate fields.
   * @param {Date} date
   * @param {string} name
   */
  const inputChangeDateHandler = (date, name) => {
    formField[name].touched = true;
    formField[name].value = date;
    formField[name].error = false;
    setFormField((prev) => ({
      ...prev,
      ...formField,
    }));
  };

  /**
   * Renders list of newly uploaded files (not yet saved to backend).
   * @returns {JSX.Element[]}
   */
  const renderFiles = () => {
    let files = [];
    for (var x = 0; x < formField.attachments.value.length; x++) {
      const index = x;
      files.push(
        <span key={x}>
          {formField.attachments.value[x].name}
          {permissions.writePermission && (
            <span onClick={() => removeFile(index, "new")}>
              <DeleteIcon />
            </span>
          )}
        </span>,
      );
    }
    return files;
  };

  /**
   * Renders list of existing attachments (already saved to backend).
   * @returns {JSX.Element[]}
   */
  const renderAttachments = () => {
    const attachments = formField.attachments.attachmentArr.map((doc, i) => (
      <div key={i} className="attachedFiles input-block-level">
        <p style={{ margin: "0 10px 0 0" }}>
          <a href={doc.url} rel="noopener noreferrer" target="_blank">
            {doc.name}
          </a>
        </p>
        {permissions.writePermission && (
          <p
            style={{ margin: "0", float: "right", cursor: "pointer" }}
            onClick={() => removeFile(i, "old")}
          >
            <DeleteIcon />
          </p>
        )}
      </div>
    ));
    return attachments;
  };

  /**
   * Removes a file from attachments (either new or old).
   * @param {number} index
   * @param {string} type - 'new' or 'old'
   */
  const removeFile = (index, type) => {
    if (type === "new") {
      const data = [];
      for (var x = 0; x < formField.attachments.value.length; x++) {
        if (x !== index) {
          data.push(formField.attachments.value[x]);
        }
      }
      formField.attachments.value = data;
    }
    if (type === "old") {
      const data = formField.attachments.attachmentArr.filter(
        (doc, i) => i !== index,
      );
      formField.attachments.attachmentArr = data;
    }
    setFormField((prev) => ({
      ...prev,
      ...formField,
    }));
  };

  /**
   * Formats form data for submission, including all fields and attachments.
   * @param {Object} fields
   * @returns {{data: FormData, error: boolean}}
   */
  const formatData = (fields) => {
    const { edit } = addProjectState;
    let siteAddress = getAddressDropdown(fields.customer.value) || [];
    let address =
      siteAddress.find((sa) => sa.value === fields.address.value) || {};
    if (!address.key) {
      dispatch(errorSnackBar(`Please select Address`));
      return { error: true };
    }

    // Use existing address_breakdown if available, otherwise build it
    let addressBreakdown = fields.address_breakdown.value;

    if (!addressBreakdown) {
      // Get the full customer site data for address_breakdown
      const customer =
        (idx(customerData, (_) => _.customers) || []).find(
          (customer) => customer.id === fields.customer.value,
        ) || {};
      const selectedSite =
        (customer.siteAddress || []).find(
          (site) => site.id === fields.address.value,
        ) || {};

      // Build address_breakdown object
      addressBreakdown = {
        address_line_1: selectedSite.address_line_1 || "",
        address_line_2: selectedSite.address_line_2 || "",
        suburb: selectedSite.suburb || "",
        state: selectedSite.state || "",
        postcode: selectedSite.postcode || "",
        country: selectedSite.country || "",
        site_name: selectedSite.name || "",
        formatted_address: [
          selectedSite.address_line_1,
          selectedSite.address_line_2,
          selectedSite.suburb,
          selectedSite.state,
          selectedSite.country,
          selectedSite.postcode,
        ]
          .filter((item) => item)
          .join(" ")
          .trim(),
      };
    }

    const formData = new FormData();
    formData.set("name", fields.projectName.value);
    if (address.key) formData.set("address", address.key);
    formData.set("address_breakdown", JSON.stringify(addressBreakdown));
    formData.set("level", fields.level.value);
    if (edit) {
      formData.set("category", fields.category.value);
    }
    formData.set("manager", fields.manager.value);
    formData.set("customer", fields.customer.value);
    formData.set("customerContact", fields.contact.value);
    formData.set("dateOfCreation", fields.date.value);
    formData.set("description", fields.description.value);
    formData.set("exclusions", fields.exclusions.value);
    formData.set("type", fields.projectType.value);
    const projectType =
      searchParams.get("project-type") ||
      idx(projectSummary, (_) => _.project.projectDataType);
    formData.set("projectType", projectType);
    formData.set("projectQuoteType", fields.projectQuoteType.value);
    formData.set("profit_margin_value", fields.profitMarginValue.value);
    formData.set("prefix", fields.prefix.value);
    formData.set("suffix", fields.suffix.value);
    formData.set("autoCalculateRetention", fields.autoCalculateRetention.value);
    formData.set("showRetentions", fields.showRetentions.value || false);
    formData.set("showVariations", fields.showVariations.value || false);
    formData.set("useRevisedProjectTotal", fields.useRevisedProjectTotal.value);
    formData.set("maxRetentionPercentage", fields.maxRetentionPercentage.value);
    formData.set(
      "defaultRetentionPercentage",
      fields.defaultRetentionPercentage.value,
    );

    formData.set("budget", fields.budget.value || 0);
    formData.set("startDate", fields.startDate.value || new Date());

    if (fields.projectQuoteType.value == "noquote" && !edit) {
      formData.set("status", "NOT_STARTED");
      formData.set("type", "PROJECT");
    } else if (fields.projectQuoteType.value !== "quote" && !edit) {
      formData.set("status", "PENDING");
      formData.set("type", "TENDER");
    }

    for (var i = 0; i < fields.attachments.attachmentArr.length; i++) {
      formData.append(
        "attachment[]",
        JSON.stringify(fields.attachments.attachmentArr[i]),
      );
    }

    for (var x = 0; x < fields.attachments.value.length; x++) {
      formData.append("attachment", fields.attachments.value[x]);
    }
    return { data: formData, error: false };
  };

  /**
   * Validates and submits the form for add/edit project, handles navigation on success.
   */
  const submitForm = async () => {
    try {
      let action = searchParams.get("action");
      let fields = _.cloneDeep(formField);

      let isFormValid = true;
      for (let key in fields) {
        if (key.includes("budget")) continue;
        let valid = true;
        let error = false;
        const { value, validation } = fields[key];
        const hasValue = value !== "" && value !== null && value !== undefined;
        if (
          !validation.includes(ValidatorTypes.SKIP) &&
          (validation.includes(ValidatorTypes.REQUIRED) || hasValue)
        ) {
          const result = ValidateField({ value, validation });
          error = result.error;
          valid = result.valid;
        }

        // Enforce retention minimums on submit
        if (key === "maxRetentionPercentage") {
          const num = Number(value);
          if (
            value !== "" &&
            value !== null &&
            value !== undefined &&
            !isNaN(num) &&
            num < 5
          ) {
            error = "Value must be at least 5";
            valid = false;
          }
        } else if (key === "defaultRetentionPercentage") {
          const num = Number(value);
          if (
            value !== "" &&
            value !== null &&
            value !== undefined &&
            !isNaN(num) &&
            num < 10
          ) {
            error = "Value must be at least 10";
            valid = false;
          }
        }

        if (typeof fields[key] == "object") {
          fields[key].touched = true;
          fields[key].valid = valid;
          fields[key].error = error;
        }
        isFormValid = isFormValid && valid;

      }

      if (!isFormValid) {
        setFormField((prev) => ({
          ...prev,
          ...fields,
        }));
      } else {
        const { error, data: formData } = formatData(fields);
        if (error) return;

        if (!projectId || (projectId && action === "revise")) {
          if (projectId && action === "revise") {
            formData.set("isRevised", true);
            formData.set("projectId", projectId);
            formData.set(
              "hasStages",
              idx(projectSummary, (_) => _.project.hasStages) || false,
            );
          }

          if (fields.projectQuoteType.value == QUOTE_TYPE.NOQUOTE) {
            formData.set("hasStages", false);
            formData.set("status", PROJECT_STATUS.NOT_STARTED);
            formData.set("type", PROJECT_TYPE.PROJECT);
          } else {
            formData.set("status", PROJECT_STATUS.COSTED);
            formData.set("type", PROJECT_TYPE.TENDER);
          }

          dispatch(addProject(formData))
            .then(async (res) => {
              if (projectId && action === "revise") {
                // await this.props.history(`${res.id}`);
              }
              if (
                (res.stages || []).length == 0 &&
                res.id &&
                action != "revise"
              ) {
                const newformData = new FormData();
                newformData.set("name", "Section 1");
                newformData.set("stageId", null);
                newformData.set("parentStageId", null);
                newformData.set("nestedStageId", null);
                newformData.set("isDummyStage", true);
                await dispatch(addStage(newformData, res.id, true));
              }
              if (formField.projectQuoteType.value == QUOTE_TYPE.NOQUOTE) {
                navigate(`/projects/view/${res.id}`);
              } else if (formField.projectQuoteType.value == QUOTE_TYPE.QUOTE) {
                navigate(`/projects/view/tender/${res.id}`);
              }
            })
            .catch((e) => {
              console.error(e);
            });
        } else {
          dispatch(editProject(formData, projectId))
            .then((res) => {
              if (
                (formField.projectQuoteType.value == QUOTE_TYPE.NOQUOTE &&
                  fields.projectType.value === PROJECT_TYPE.PROJECT) ||
                (formField.projectQuoteType.value == QUOTE_TYPE.QUOTE &&
                  fields.projectType.value === PROJECT_TYPE.PROJECT)
              ) {
                navigate(`/projects/view/${projectId}`);
              } else if (
                formField.projectQuoteType.value == QUOTE_TYPE.QUOTE ||
                (formField.projectQuoteType.value == QUOTE_TYPE.NOQUOTE &&
                  fields.projectType.value === PROJECT_TYPE.TENDER)
              ) {
                navigate(`/projects/view/tender/${projectId}`);
              }
            })
            .catch((e) => console.error(e));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Handles conversion of a tender to a project, with validation and submission.
   */
  const convertToProject = () => {
    let fields = _.cloneDeep(formField);
    let isFormValid = true;
    for (let key in fields) {
      const { value, validation } = fields[key];
      const { error, valid } = ValidateField({ value, validation });
      fields[key] = { ...fields[key], error, valid, touched: true };
      if (!valid) {
        isFormValid = false;
      }
    }

    if (!isFormValid) {
      setFormField((prev) => ({
        ...prev,
        ...fields,
      }));
    } else {
      const { error, data: formData } = formatData(fields);
      if (error) return;

      formData.set("status", PROJECT_STATUS.IN_PROGRESS);
      formData.set("type", PROJECT_TYPE.PROJECT);
      dispatch(editProject(formData, projectId))
        .then(async (res) => {
          props.handleClose();
          await getProjectData({ projectId, dispatch });
          navigate(`/projects/view/${res.id}`);
        })
        .catch((e) => console.error(e));
      setFormField(fields);
      return;
    }

    const data = formatData(fields);

    data.data.set("status", "IN_PROGRESS");
    data.data.set("type", "PROJECT");
    dispatch(editProject(data.data, projectId))
      .then(async (res) => {
        props.handleClose();
        await getProjectData({ projectId, dispatch });
        navigate(`/projects/view/${res.id}`);
      })
      .catch((e) => {
        console.error("Error converting project:", e);
      });
  };

  const projectType =
    searchParams.get("project-type") ||
    idx(projectSummary, (_) => _.project.projectType) ||
    "Project";
  const ProjectFieldType = projectType === PROJECT_TYPE.TENDER ? "Tender" : "Project";
  const inProgressQuote =
    idx(projectSummary, (_) => _.project.quoteType) === QUOTE_TYPE.QUOTE &&
    idx(projectSummary, (_) => _.project.res.status) === PROJECT_STATUS.IN_PROGRESS;

  /**
   * Returns the page title based on add/edit and project/tender mode.
   * @returns {string}
   */
  const getProjectTitle = () => {
    if (projectId && idx(projectSummary, (_) => _.project.res.id)) {
      return projectType === PROJECT_TYPE.TENDER ? "Edit a Tender" : "Edit a Project";
    } else {
      return projectType === PROJECT_TYPE.TENDER ? "Add a Tender" : "Add a Project";
    }
  };

  return (
    <>
      <div className="purchase-order-block">
        {!addProjectState.convertTender && (
          <>
            <h2 className="content-heading text-uppercase">
              {getProjectTitle()}
            </h2>
            <div className="col-12 breadcrumb-block p-0">
              <Breadcrumb>
                <Breadcrumb.Item onClick={() => cancelHandler()}>
                  PROJECT{" "}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{getProjectTitle()}</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </>
        )}

        {isConvertToProject() && (
          <div className="col-12 alert alert-warning mt-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Please double check your quote details before converting to a
            project
          </div>
        )}

        <div className="row">
          <div className="col-lg-4 mt-3">
            <CustomInput
              {...formField.projectName}
              label={`Select ${ProjectFieldType} Name`}
              disabled={!permissions.writePermission}
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>

          {!addProjectState.convertTender && (
            <div className="col-lg-4 mt-3">
              <DatePickerInput
                {...formField.date}
                disabled={!permissions.writePermission}
                onChange={(e) => inputChangeDateHandler(e, "date")}
              />
            </div>
          )}

          {addProjectState.edit && (
            <div className="col-lg-4 mt-3">
              <SelectInput
                {...formField.category}
                label={`Select ${ProjectFieldType} Category`}
                disabled={!permissions.writePermission}
                onChange={(e) => inputChangeHandler(e)}
              />
            </div>
          )}
          <div className="col-lg-4 mt-3">
            <SelectInput
              {...formField.projectQuoteType}
              label={`Select ${ProjectFieldType} Type`}
              disabled={!permissions.writePermission}
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>
          <div className="col-lg-4 mt-3">
            <SelectInput
              {...formField.manager}
              label={`Select ${ProjectFieldType} Manager`}
              disabled={!permissions.writePermission}
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>
          <div className="col-lg-4 mt-3">
            <div className="d-flex">
              <SelectInput
                {...formField.customer}
                items={idx(customerData, (_) => _.dropdown)}
                label={"Select Customer"}
                disabled={!permissions.writePermission}
                onChange={(e) => inputChangeCustomer(e)}
              />
              {!projectId && permissions.writePermission && (
                <span className="download-icon addicon ms-2 mt-2-5">
                  <AddIcon onClick={() => addNewCustomer()} />
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-4 mt-3">
            <div className="d-flex">
              <div className="flex-grow-1">
                <SelectInput
                  {...formField.contact}
                  items={getContactDropdown()}
                  label={"Select Customer Contact"}
                  disabled={!permissions.writePermission}
                  onChange={(e) => inputChangeHandler(e)}
                />
              </div>
              {!projectId && permissions.writePermission && (
                <span className="download-icon addicon ms-2 mt-2-5">
                  <AddIcon
                    onClick={() => addNewCustomer(formField.customer.value)}
                  />
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-4 mt-3">
            <div className="d-flex">
              <div className="flex-grow-1">
                <SelectInput
                  {...formField.address}
                  items={getAddressDropdown()}
                  label={"Select Address"}
                  disabled={!permissions.writePermission}
                  onChange={(e) => inputChangeAddress(e)}
                />
              </div>
              {!projectId && permissions.writePermission && (
                <span className="download-icon addicon ms-2 mt-2-5">
                  <AddIcon
                    onClick={() => addNewCustomer(formField.customer.value)}
                  />
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-4 mt-3">
            <SelectInput
              {...formField.level}
              label={"Select Cost/Sales Level"}
              disabled={!permissions.writePermission}
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>
          <div className="col-lg-4 mt-3 text-left">
            <CustomInput
              {...formField.profitMarginValue}
              disabled={inProgressQuote}
              onChange={(e) => inputChangeHandler(e)}
            />
            {!formField.profitMarginValue.error && (
              <p className="help-text mt-1 mb-0">
                {" "}
                Default profit margin added to Material and Sub-Contractors
              </p>
            )}
          </div>
          <div className="col-lg-4 mt-3 text-left">
            <CustomInput
              {...formField.prefix}
              disabled={
                !permissions.writePermission ||
                (addProjectState.edit && projectType !== PROJECT_TYPE.TENDER) ||
                isConvertToProject()
              }
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>
          <div className="col-lg-4 mt-3 text-left">
            <CustomInput
              {...formField.suffix}
              disabled={
                !permissions.writePermission ||
                (addProjectState.edit && projectType !== PROJECT_TYPE.TENDER) ||
                isConvertToProject()
              }
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>
          <div className="col-12">
            <div className="row">
              <div className="col-lg-3 col-md-6 d-flex mt-3 text-left align-items-center">
                <Checkbox
                  {...formField.showRetentions}
                  checked={formField.showRetentions.value}
                  value={formField.showRetentions.value}
                  disabled={!permissions.writePermission}
                  onChange={(e) => inputChangeHandler(e)}
                  className="show-retentions-checkbox"
                  id="showRetentions"
                  data-cy={formField.showRetentions["data-cy"]}
                />
                <label htmlFor="showRetentions" className="">
                  Show Retention
                </label>
              </div>
              <div className="col-lg-3 col-md-6 d-flex mt-3 text-left align-items-center">
                <Checkbox
                  {...formField.showVariations}
                  checked={formField.showVariations.value}
                  value={formField.showVariations.value}
                  disabled={!permissions.writePermission}
                  onChange={(e) => inputChangeHandler(e)}
                  className="show-variations-checkbox"
                  id="showVariations"
                  data-cy={formField.showVariations["data-cy"]}
                />
                <label htmlFor="showVariations" className="">
                  Show Variation
                </label>
              </div>
              <div className="col-lg-3 col-md-6 d-flex mt-3 text-left align-items-center">
                <Checkbox
                  {...formField.useRevisedProjectTotal}
                  checked={formField.useRevisedProjectTotal.value}
                  value={formField.useRevisedProjectTotal.value}
                  disabled={!permissions.writePermission}
                  onChange={(e) => inputChangeHandler(e)}
                  className="use-revised-total-checkbox"
                  id="useRevisedProjectTotal"
                  data-cy={formField.useRevisedProjectTotal["data-cy"]}
                />
                <label htmlFor="useRevisedProjectTotal" className="">
                  Use Revised Project Total
                </label>
              </div>
            </div>
          </div>
          {formField.showRetentions.value && (
            <div className="col-12">
              <div className="row">
                {/** Checkbox to enable or disable retention calculation */}

                <div className="col-lg-3 col-md-6 d-flex mt-3 text-left align-items-center">
                  <Checkbox
                    {...formField.autoCalculateRetention}
                    checked={formField.autoCalculateRetention.value}
                    value={formField.autoCalculateRetention.value}
                    disabled={!permissions.writePermission}
                    onChange={(e) => inputChangeHandler(e)}
                    className="auto-calculate-retention-checkbox"
                    id="autoCalculateRetention"
                    data-cy={formField.autoCalculateRetention["data-cy"]}
                  />
                  <label htmlFor="autoCalculateRetention" className="">
                    Auto Calculate Retention
                  </label>
                </div>
                <div className="col-lg-4 col-md-6 mt-3 text-left">
                  <CustomInput
                    {...formField.maxRetentionPercentage}
                    disabled={!permissions.writePermission}
                    onChange={(e) => inputChangeHandler(e)}
                  />
                </div>
                <div className="col-lg-4 col-md-6 mt-3 text-left">
                  <CustomInput
                    {...formField.defaultRetentionPercentage}
                    disabled={!permissions.writePermission}
                    onChange={(e) => inputChangeHandler(e)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-lg-6 col-md-6 mt-3">
            <TextAreaInput
              {...formField.description}
              disabled={!permissions.writePermission || isConvertToProject()}
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>
          {formField.projectQuoteType.value !== QUOTE_TYPE.NOQUOTE && (
            <div className="col-lg-6 col-md-6 mt-3">
              <TextAreaInput
                {...formField.exclusions}
                disabled={!permissions.writePermission || isConvertToProject()}
                onChange={(e) => inputChangeHandler(e)}
              />
            </div>
          )}
        </div>
        <div className="row">
          {permissions.writePermission && (
            <div className="col-lg-6 col-md-6 mt-3 fileupload-section">
              <div className="file-section-part position-relative h-auto px-0">
                <div className="file-part">
                  <FileUploader
                    title="Attach Project Documents"
                    fileHandler={(files) =>
                      inputChangeHandler({
                        target: {
                          files,
                          type: "file",
                          name: "attachments",
                        },
                      })
                    }
                  />
                  <label htmlFor="#file-section"></label>
                </div>
                <input type="file" id="file-section"></input>
              </div>
            </div>
          )}
          <div className="col-lg-6 col-md-6 mt-3">
            <div className="form-group">
              <div className="input-block">
                <label className="input-label">Uploaded Documents</label>
                <p className="m-0 doc-info text-left input-block-level">
                  {renderFiles()}
                  {renderAttachments()}
                </p>
              </div>
            </div>
          </div>
          {!addProjectState.convertTender && (
            <div className="col-12 d-flex mt-3">
              <Button
                onClick={() => submitForm()}
                className="ms-auto me-3 secondarybtn"
                data-cy="submit-button"
              >
                {searchParams.get("action") === "revise" ? "save" : "Next"}
              </Button>
              <Button
                onClick={() => cancelHandler()}
                className="secondarybtn cancelbtn me-auto"
                data-cy="cancel-button"
              >
                CANCEL
              </Button>
            </div>
          )}
          {addProjectState.convertTender && (
            <div className="col-12 d-flex mt-3">
              <Button
                onClick={() => {
                  if (hasAttributionEnabled) {
                    setAddProjectState((prev) => ({
                      ...prev,
                      showConfirmConvertModal: true,
                    }));
                  } else {
                    convertToProject();
                  }
                }}
                className="ms-auto me-3 secondarybtn"
                data-cy="convert-button"
              >
                Convert
              </Button>
              <Button
                onClick={props.handleClose}
                className="secondarybtn cancelbtn me-auto"
                data-cy="cancel-button"
              >
                CANCEL
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Attribution Confirmation Modal */}
      {addProjectState.showConfirmConvertModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
          ></div>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h4 style={{ margin: 0 }}>Activate Project</h4>
            </div>
            <div>
              <p>
                You are about to activate this project. Once activated, cost
                attribution will be locked to the flagged section levels and{" "}
                <strong>cannot be changed</strong>.
              </p>
              <p>
                This project has one or more sections flagged for cost
                attribution. All future costs will be attributed at these
                section levels rather than to child sections or tasks. This
                setting is <strong>permanent and cannot be reversed</strong>{" "}
                after activation.
              </p>

              <div className="alert alert-danger" style={{ marginTop: "20px" }}>
                <strong>Are you sure you want to activate this project?</strong>
              </div>
            </div>
            <div style={{ marginTop: "20px", textAlign: "right" }}>
              <button
                onClick={() => {
                  setAddProjectState((prev) => ({
                    ...prev,
                    showConfirmConvertModal: false,
                  }));
                }}
                style={{
                  marginRight: "10px",
                  padding: "8px 16px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setAddProjectState((prev) => ({
                    ...prev,
                    showConfirmConvertModal: false,
                  }));
                  convertToProject();
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
